import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table } from '../../../components/table/Table';
import { fetchCustomerPortalInvoices } from './models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { decimateAndFormatNumber } from '../../../utils/number';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { getInvoiceTotals } from '../../../domain/invoice-totals';
import { useTranslation } from '../../../contexts/translation-context';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';

export const CustomerPortalInvoicesPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const { i18n } = useTranslation();
  const { customer } = useCustomerPortalCustomer();
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return fetchCustomerPortalInvoices({
        customerId: customer.id,
        cursor: variables.cursor,
        take: variables.take,
        filters: {
          search: searchValue,
        },
      });
    },
    [customer.id, searchValue],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const tableHeaders: ITableHeader[] = [
    {
      id: 'id',
      name: i18n('customerPortal.invoices.invoiceNumber'),
    },
    {
      id: 'date',
      name: i18n('customerPortal.invoices.invoiceDate'),
    },
    {
      id: 'dueDate',
      name: i18n('customerPortal.invoices.dueDate'),
    },
    {
      id: 'total',
      name: i18n('customerPortal.invoices.total'),
    },
  ];

  useEffect(() => {
    page.reset();
  }, [searchValue]);

  const title = i18n('customerPortal.invoices.invoices');
  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={<PageHeading noMarginBottom leftSide={<Breadcrumb currentItem={title} />} />}
        filters={
          <Input
            type="text"
            placeholder={i18n('customerPortal.invoices.search')}
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(invoice) => {
            const totals = getInvoiceTotals([
              ...invoice.orders
                .map((o) =>
                  o.lines
                    .map((l) =>
                      l.sales.map((s) => {
                        return {
                          ...s,
                          vatRateId: s.vatRate.id,
                        };
                      }),
                    )
                    .flat(),
                )
                .flat(),
              ...invoice.manualInvoiceLines.map((l) => {
                return {
                  ...l,
                  vatRateId: l.vatRate.id,
                };
              }),
            ]);

            return [
              <Link to={`${invoice.id}`} className="link-text">
                {invoice.invoiceNumber ?? i18n('draft')}
              </Link>,
              formatDate(invoice.date),
              invoice.expiresAt ? formatDate(invoice.expiresAt) : '-',
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(totals.totalInclVat, 2, {
                decimalSeperator: ',',
              })}`}</div>,
            ];
          }}
        />
      </ListPage>
    </>
  );
};
